import { useEffect, useState } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../auth/AuthContext';
import { getCourse } from '../../api/instructorApi';
import { toTitleCase } from '../../utils/format';

import '../../styles/instructor/instructor-layout.css';
import '../../styles/instructor/instructor-components.css';

function initials(name = '') {
  return name.split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase();
}

export default function InstructorLayout() {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarData, setSidebarData] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);

  function navTo(path) { navigate(path); setSidebarOpen(false); }

  // Extract courseId from URL
  const courseMatch = location.pathname.match(/\/instructor\/courses\/(\d+)/);
  const courseId = courseMatch ? Number(courseMatch[1]) : null;

  // Extract moduleId from URL
  const moduleMatch = location.pathname.match(/\/courses\/\d+\/modules\/(\d+)/);
  const currentModuleId = moduleMatch ? Number(moduleMatch[1]) : null;

  useEffect(() => {
    if (courseId) {
      getCourse(courseId)
        .then(d => setSidebarData(d))
        .catch(() => setSidebarData(null));
    } else {
      setSidebarData(null);
    }
  }, [courseId]);

  const path = location.pathname;
  const isDashboard   = path === '/instructor/dashboard';
  const isOnStudents  = /\/courses\/\d+\/students/.test(path);
  const isOnAssign    = /\/courses\/\d+\/assignments\/\d+\/submissions/.test(path);
  const isOnModule    = /\/courses\/\d+\/modules\/\d+/.test(path);
  const isOnCourse    = courseId && !isOnStudents && !isOnAssign && !isOnModule;

  const course  = sidebarData?.course;
  const modules = sidebarData?.modules || [];

  // Show course-related sidebar links when we have a course context
  const hasCourseContext = !!courseId;

  return (
    <div className="app">
      {/* ── Mobile top bar ── */}
      <div className="mob-bar">
        <div className="mob-logo">
          <div className="sb-mark"><svg viewBox="0 0 14 14" fill="none"><polygon points="7,1 13,13 1,13" fill="#0C0B09" /></svg></div>
          <div className="sb-brand">AI <span>Foundry</span></div>
        </div>
        <button className={`hamburger${sidebarOpen ? ' open' : ''}`} onClick={() => setSidebarOpen(o => !o)}>
          <span /><span /><span />
        </button>
      </div>

      {/* ── Sidebar overlay ── */}
      <div className={`sb-overlay${sidebarOpen ? ' open' : ''}`} onClick={() => setSidebarOpen(false)} />

      {/* ── Sidebar ── */}
      <aside className={`sb${sidebarOpen ? ' open' : ''}`}>
        <div className="sb-logo">
          <div className="sb-mark">
            <svg viewBox="0 0 14 14" fill="none">
              <polygon points="7,1 13,13 1,13" fill="#0C0B09" />
            </svg>
          </div>
          <div className="sb-brand">AI <span>Foundry</span></div>
          <button onClick={() => setSidebarOpen(false)} className="sb-close" style={{ marginLeft: 'auto', background: 'none', border: 'none', color: '#5A5650', cursor: 'pointer', fontSize: 18, padding: '2px 4px', display: 'none' }}>✕</button>
        </div>

        <div className="sb-sec">
          <div className="sb-lbl">Teaching</div>

          <div
            className={`sb-link${isDashboard ? ' on' : ''}`}
            onClick={() => navTo('/instructor/dashboard')}
          >
            <span className="ic">⊞</span> My Courses
          </div>

          {hasCourseContext && course && (
            <>
              <div className="sb-divider" />

              {/* Course title link */}
              <div
                className={`sb-link${isOnCourse ? ' on' : ''}`}
                onClick={() => navTo(`/instructor/courses/${courseId}`)}
                style={{ fontWeight: 600, color: '#F8F7F3' }}
              >
                <span className="ic">{course.emoji}</span>
                <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {toTitleCase(course.title)}
                </span>
              </div>

              {/* Module sub-links (only when on course or module pages) */}
              {(isOnCourse || isOnModule) && modules.map(m => {
                const isActiveModule = isOnModule && currentModuleId === m.id;
                return (
                  <div
                    key={m.id}
                    className={`sb-link${isActiveModule ? ' on' : ''}`}
                    style={{ paddingLeft: 34, fontSize: 11.5 }}
                    onClick={() => navTo(`/instructor/courses/${courseId}/modules/${m.id}`)}
                  >
                    <span style={{ fontSize: 10 }}>{m.status === 'published' ? '✓' : '○'}</span>
                    <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {toTitleCase(m.title)}
                    </span>
                  </div>
                );
              })}

              <div className="sb-divider" />

              {/* Assignments link */}
              <div
                className={`sb-link${isOnAssign ? ' on' : ''}`}
                onClick={() => navTo(`/instructor/courses/${courseId}?tab=assignments`)}
              >
                <span className="ic">📋</span> Assignments
              </div>

              {/* Students link */}
              <div
                className={`sb-link${isOnStudents ? ' on' : ''}`}
                onClick={() => navTo(`/instructor/courses/${courseId}/students`)}
              >
                <span className="ic">◈</span> Students
              </div>
            </>
          )}
        </div>

        {/* ── Footer ── */}
        <div className="sb-foot">
          <div className="av" style={{ background: 'var(--orange)', color: 'white' }}>
            {user ? initials(user.full_name) : 'IN'}
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div className="av-name">{toTitleCase(user?.full_name || 'Instructor')}</div>
            <div className="av-role">Instructor</div>
          </div>
          <button
            onClick={logout}
            title="Log out"
            style={{
              background: 'none', border: 'none', cursor: 'pointer',
              color: '#5A5650', padding: '4px',
              borderRadius: 4, lineHeight: 1, flexShrink: 0,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              transition: 'color 0.15s',
            }}
            onMouseEnter={e => e.currentTarget.style.color = '#F8F7F3'}
            onMouseLeave={e => e.currentTarget.style.color = '#5A5650'}
          >
            <svg viewBox="0 0 24 24" fill="none" width="17" height="17" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M14 3h5a1 1 0 0 1 1 1v16a1 1 0 0 1-1 1h-5" />
              <line x1="10" y1="12" x2="2" y2="12" />
              <polyline points="6 8 2 12 6 16" />
            </svg>
          </button>
        </div>
      </aside>

      {/* ── Main Content ── */}
      <div className="main">
        <Outlet />
      </div>
    </div>
  );
}
